import React, { useState, useEffect, useCallback, useRef } from 'react';

import classNames from 'classnames';
import {
  Clock,
  Download,
  Filter,
  Loader2,
  Phone,
  PhoneIncoming,
  PhoneOff,
  ExternalLink,
  Search,
  X,
} from 'lucide-react';
import { useQuery } from 'react-query';

import {
  fetchCallCenterOverview,
  fetchCallsWaiting,
  fetchAgentsStatus,
  fetchCallLogs,
  fetchAbandonedCalls,
  CallLog,
  AbandonedCall,
} from '../api';
import { PHONE_ANALYTICS_VIEW, PhoneAnalyticsView } from '../constants';

import RecordingPlayer from './RecordingPlayer';

const PAGE_SIZE = 20;

const DEFAULT_FILTERS = { search: '', dateFrom: '', dateTo: '' };

const openStandaloneWindow = (accountId: number): void => {
  const standaloneUrl = `${window.location.origin}/app/phone-settings/analytics/${accountId}`;
  window.open(
    standaloneUrl,
    'PhoneAnalytics',
    'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
  );
};

const formatDate = (timestamp?: string): string => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTimeFromTimestamp = (timestamp?: string): string => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds === 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

export const PhoneAnalytics: React.FC<{
  accountId: number;
  isStandalone?: boolean;
  activeView?: PhoneAnalyticsView;
  setActiveView?: (view: PhoneAnalyticsView) => void;
}> = ({ accountId, isStandalone = false, activeView, setActiveView }) => {
  // Estado interno: standalone, o embebido sin padre controlador (p. ej. AccountDetail solo pasa accountId)
  const [internalActiveView, setInternalActiveView] = useState<PhoneAnalyticsView>(
    PHONE_ANALYTICS_VIEW.GENERAL_SUMMARY
  );

  const useInternalViews = isStandalone || setActiveView == null;
  const currentView = useInternalViews
    ? internalActiveView
    : activeView ?? PHONE_ANALYTICS_VIEW.GENERAL_SUMMARY;
  const handleSetView = useInternalViews ? setInternalActiveView : setActiveView;

  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Call history state
  const [outboundCalls, setOutboundCalls] = useState<CallLog[]>([]);
  const [inboundCalls, setInboundCalls] = useState<CallLog[]>([]);
  const [outboundPage, setOutboundPage] = useState(1);
  const [inboundPage, setInboundPage] = useState(1);
  const [outboundHasMore, setOutboundHasMore] = useState(true);
  const [inboundHasMore, setInboundHasMore] = useState(true);
  const [isLoadingOutbound, setIsLoadingOutbound] = useState(false);
  const [isLoadingInbound, setIsLoadingInbound] = useState(false);

  // Abandoned calls state
  const [abandonedCalls, setAbandonedCalls] = useState<AbandonedCall[]>([]);
  const [abandonedPage, setAbandonedPage] = useState(1);
  const [abandonedHasMore, setAbandonedHasMore] = useState(true);
  const [isLoadingAbandoned, setIsLoadingAbandoned] = useState(false);

  // Filter state
  const [outboundFilters, setOutboundFilters] = useState(DEFAULT_FILTERS);
  const [inboundFilters, setInboundFilters] = useState(DEFAULT_FILTERS);
  const [abandonedFilters, setAbandonedFilters] = useState(DEFAULT_FILTERS);
  const [showOutboundFilters, setShowOutboundFilters] = useState(false);
  const [showInboundFilters, setShowInboundFilters] = useState(false);
  const [showAbandonedFilters, setShowAbandonedFilters] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isGeneralSummaryTab = currentView === PHONE_ANALYTICS_VIEW.GENERAL_SUMMARY;

  interface AnalyticsDisplayData {
    callsWaiting: number;
    agentsBusy: number;
    agentsAvailable: number;
    agentsOffline: number;
    agentsAssigned: number;
    agentsTotal: number;
    answeredCalls: number;
    abandonedCalls: number;
    abandonedPercentage: number;
    averageWaitTimeSeconds: number;
    maximumWaitTimeSeconds: number;
  }

  // Fetch call center overview data (polling removed to reduce API load)
  const { data: analyticsData } = useQuery<AnalyticsDisplayData>({
    queryKey: ['callCenterOverview', accountId],
    queryFn: async () => {
      const data = await fetchCallCenterOverview(accountId);
      setLastUpdated(new Date());
      return {
        callsWaiting: data.calls_waiting || 0,
        agentsBusy: data.agents?.busy || 0,
        agentsAvailable: data.agents?.available || 0,
        agentsOffline: data.agents?.offline || 0,
        agentsAssigned: data.agents?.assigned || 0,
        agentsTotal: data.agents?.total || 0,
        answeredCalls: data.answered_calls_today || 0,
        abandonedCalls: data.abandoned_calls_today?.count || 0,
        abandonedPercentage: data.abandoned_calls_today?.percentage || 0,
        averageWaitTimeSeconds: data.average_wait_time_seconds || 0,
        maximumWaitTimeSeconds: data.maximum_wait_time_seconds || 0,
      };
    },
    enabled: isGeneralSummaryTab,
    refetchOnWindowFocus: false,
    retry: false,
  });

  interface CallsWaitingDisplayData {
    total: number;
    calls: Array<{
      uuid: string;
      queue: string;
      position: number;
      caller_id: string;
      wait_time_seconds: number;
    }>;
  }

  interface AgentsStatusDisplayData {
    summary: {
      available: number;
      busy: number;
      ringing: number;
      total: number;
    };
    agents: Array<{
      agent_id: string;
      agent_name: string;
      extension: string;
      current_state: string;
      state_duration_seconds: number;
      last_call_seconds_ago: number;
      calls_handled_today: number;
    }>;
  }

  // Fetch calls waiting data (polling removed to reduce API load)
  const { data: callsWaitingData } = useQuery<CallsWaitingDisplayData>({
    queryKey: ['callsWaiting', accountId],
    queryFn: async () => {
      const data = await fetchCallsWaiting(accountId);
      return {
        total: data.total || 0,
        calls: data.calls || [],
      };
    },
    enabled: isGeneralSummaryTab,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Fetch agents status data (polling removed to reduce API load)
  const { data: agentsStatusData } = useQuery<AgentsStatusDisplayData>({
    queryKey: ['agentsStatus', accountId],
    queryFn: async () => {
      const data = await fetchAgentsStatus(accountId);
      return {
        summary: data.summary || { available: 0, busy: 0, ringing: 0, total: 0 },
        agents: data.agents || [],
      };
    },
    enabled: isGeneralSummaryTab,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Default values when data is not yet loaded
  const displayData = analyticsData || {
    callsWaiting: 0,
    agentsBusy: 0,
    agentsAvailable: 0,
    agentsOffline: 0,
    agentsAssigned: 0,
    agentsTotal: 0,
    answeredCalls: 0,
    abandonedCalls: 0,
    abandonedPercentage: 0,
    averageWaitTimeSeconds: 0,
    maximumWaitTimeSeconds: 0,
  };

  // Fetch outbound calls
  const fetchOutboundCallsData = useCallback(
    async (page = 1, reset = false, filters = {}) => {
      setIsLoadingOutbound(true);
      try {
        const data = await fetchCallLogs(accountId, page, PAGE_SIZE, 'outbound', filters);
        const calls = data.call_logs || [];
        const totalCount = data.total_count || 0;
        if (reset) {
          setOutboundCalls(calls);
          setOutboundHasMore(calls.length < totalCount);
        } else {
          setOutboundCalls((prev) => {
            const existingIds = new Set(prev.map((c) => c.uuid || c.id));
            const unique = calls.filter((c) => !existingIds.has(c.uuid || c.id));
            const newCalls = [...prev, ...unique];
            setOutboundHasMore(newCalls.length < totalCount);
            return newCalls;
          });
        }
        setOutboundPage(page);
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoadingOutbound(false);
      }
    },
    [accountId]
  );

  // Fetch abandoned calls
  const fetchAbandonedCallsData = useCallback(
    async (page = 1, reset = false, filters = {}) => {
      setIsLoadingAbandoned(true);
      try {
        const data = await fetchAbandonedCalls(accountId, page, PAGE_SIZE, filters);
        const calls = data.abandoned_calls || [];
        const totalCount = data.total_count || 0;
        if (reset) {
          setAbandonedCalls(calls);
          setAbandonedHasMore(calls.length < totalCount);
        } else {
          setAbandonedCalls((prev) => {
            const existingIds = new Set(prev.map((c) => c.uuid || c.id));
            const unique = calls.filter((c) => !existingIds.has(c.uuid || c.id));
            const newCalls = [...prev, ...unique];
            setAbandonedHasMore(newCalls.length < totalCount);
            return newCalls;
          });
        }
        setAbandonedPage(page);
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoadingAbandoned(false);
      }
    },
    [accountId]
  );

  // Fetch inbound calls
  const fetchInboundCallsData = useCallback(
    async (page = 1, reset = false, filters = {}) => {
      setIsLoadingInbound(true);
      try {
        const data = await fetchCallLogs(accountId, page, PAGE_SIZE, 'inbound', filters);
        const calls = data.call_logs || [];
        const totalCount = data.total_count || 0;
        if (reset) {
          setInboundCalls(calls);
          setInboundHasMore(calls.length < totalCount);
        } else {
          setInboundCalls((prev) => {
            const existingIds = new Set(prev.map((c) => c.uuid || c.id));
            const unique = calls.filter((c) => !existingIds.has(c.uuid || c.id));
            const newCalls = [...prev, ...unique];
            setInboundHasMore(newCalls.length < totalCount);
            return newCalls;
          });
        }
        setInboundPage(page);
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoadingInbound(false);
      }
    },
    [accountId]
  );

  // Load calls when switching to history tabs
  useEffect(() => {
    if (currentView === PHONE_ANALYTICS_VIEW.OUTBOUND_HISTORY) {
      fetchOutboundCallsData(1, true, outboundFilters);
    }
  }, [currentView, fetchOutboundCallsData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentView === PHONE_ANALYTICS_VIEW.INBOUND_HISTORY) {
      fetchInboundCallsData(1, true, inboundFilters);
    }
  }, [currentView, fetchInboundCallsData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentView === PHONE_ANALYTICS_VIEW.ABANDONED_CALLS) {
      fetchAbandonedCallsData(1, true, abandonedFilters);
    }
  }, [currentView, fetchAbandonedCallsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup search debounce on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Handle filter changes with debounce for search
  const handleFilterChange = useCallback(
    (direction: 'outbound' | 'inbound' | 'abandoned', field: string, value: string) => {
      const filterSetterMap = {
        outbound: setOutboundFilters,
        inbound: setInboundFilters,
        abandoned: setAbandonedFilters,
      };
      const fetchFnMap = {
        outbound: fetchOutboundCallsData,
        inbound: fetchInboundCallsData,
        abandoned: fetchAbandonedCallsData,
      };
      const currentFiltersMap = {
        outbound: outboundFilters,
        inbound: inboundFilters,
        abandoned: abandonedFilters,
      };
      const setFilters = filterSetterMap[direction];
      const fetchFn = fetchFnMap[direction];
      const newFilters = { ...currentFiltersMap[direction], [field]: value };

      setFilters(newFilters);

      if (field === 'search' || field === 'extension') {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
          fetchFn(1, true, newFilters);
        }, 400);
      } else {
        fetchFn(1, true, newFilters);
      }
    },
    [
      fetchOutboundCallsData,
      fetchInboundCallsData,
      fetchAbandonedCallsData,
      outboundFilters,
      inboundFilters,
      abandonedFilters,
    ]
  );

  const handleClearFilters = useCallback(
    (direction: 'outbound' | 'inbound' | 'abandoned') => {
      const filterSetterMap = {
        outbound: setOutboundFilters,
        inbound: setInboundFilters,
        abandoned: setAbandonedFilters,
      };
      const fetchFnMap = {
        outbound: fetchOutboundCallsData,
        inbound: fetchInboundCallsData,
        abandoned: fetchAbandonedCallsData,
      };
      const setFilters = filterSetterMap[direction];
      const fetchFn = fetchFnMap[direction];
      setFilters(DEFAULT_FILTERS);
      fetchFn(1, true, DEFAULT_FILTERS);
    },
    [fetchOutboundCallsData, fetchInboundCallsData, fetchAbandonedCallsData]
  );

  // Download calls as CSV
  const handleDownloadCsv = useCallback(
    async (direction: 'outbound' | 'inbound') => {
      setIsDownloading(true);
      try {
        const filters = direction === 'outbound' ? outboundFilters : inboundFilters;

        // Fetch first page to determine total
        const firstResponse = await fetchCallLogs(accountId, 1, 100, direction, filters);
        const firstCalls = firstResponse.call_logs || [];
        const totalCount = firstResponse.total_count || 0;
        const totalPages = Math.ceil(totalCount / 100);

        // Fetch remaining pages in batches
        const BATCH_SIZE = 5;
        const remainingCalls: CallLog[] = [];
        for (let i = 2; i <= totalPages; i += BATCH_SIZE) {
          const batch = [];
          for (let p = i; p < Math.min(i + BATCH_SIZE, totalPages + 1); p += 1) {
            batch.push(fetchCallLogs(accountId, p, 100, direction, filters));
          }
          const batchResponses = await Promise.all(batch); // eslint-disable-line no-await-in-loop
          remainingCalls.push(...batchResponses.flatMap((r) => r.call_logs || []));
        }
        const allCalls = [...firstCalls, ...remainingCalls];

        const escapeCsvField = (field: unknown): string => {
          const str = String(field ?? '');
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };

        const headers = [
          'Número de teléfono',
          'Fecha',
          'Hora',
          'Duración',
          'Agente',
          'Estado',
          'Nota de resolución',
          'Tiene grabación',
        ];

        const rows = allCalls.map((call) => {
          const phoneNumber =
            direction === 'outbound'
              ? call.destination_number || call.callee_id_number || ''
              : call.caller_id_number || call.caller_id_name || '';
          return [
            phoneNumber,
            formatDate(call.start_stamp || call.created_at),
            formatTimeFromTimestamp(call.start_stamp || call.created_at),
            formatDuration(call.billsec || call.duration || 0),
            call.agent_name || call.user_name || '-',
            call.hangup_cause || call.status || '-',
            call.resolution_note || '',
            call.recording_url ? 'Sí' : 'No',
          ];
        });

        const csvContent = [
          headers.map(escapeCsvField).join(','),
          ...rows.map((row) => row.map(escapeCsvField).join(',')),
        ].join('\n');

        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const dateStr = new Date().toISOString().split('T')[0];
        link.download = `${direction}-calls-${dateStr}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        // Handle error silently
      } finally {
        setIsDownloading(false);
      }
    },
    [accountId, outboundFilters, inboundFilters]
  );

  // Format seconds to human readable format (Xh Xm Xs)
  const formatSecondsToTime = (seconds: number): string => {
    if (!seconds || seconds === 0) return '0m 0s';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.round(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Get status badge style
  const getCallStatusStyle = (status: string): { color: string; bg: string; label: string } => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('answer') || statusLower === 'completed') {
      return { color: 'text-green-700', bg: 'bg-green-100', label: 'Contestada' };
    }
    if (
      statusLower.includes('miss') ||
      statusLower === 'no_answer' ||
      statusLower === 'no-answer'
    ) {
      return { color: 'text-red-700', bg: 'bg-red-100', label: 'Perdida' };
    }
    if (statusLower.includes('voicemail')) {
      return { color: 'text-blue-700', bg: 'bg-blue-100', label: 'Buzón de voz' };
    }
    if (statusLower.includes('transfer')) {
      return { color: 'text-purple-700', bg: 'bg-purple-100', label: 'Transferida' };
    }
    return { color: 'text-gray-700', bg: 'bg-gray-100', label: status || '-' };
  };

  // Format seconds ago to "Xm ago" or "Xs ago" format
  const formatSecondsAgo = (seconds: number): string => {
    if (!seconds || seconds === 0) return '-';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return `${seconds}s hace`;
    if (minutes < 60) return `${minutes}m hace`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h hace`;
    const days = Math.floor(hours / 24);
    return `${days}d hace`;
  };

  // Get state color and label for agent status
  const getStateStyle = (currentState: string): { color: string; dot: string; label: string } => {
    const stateMap: Record<string, { color: string; dot: string; label: string }> = {
      'Outbound Call': {
        color: 'text-red-600',
        dot: 'bg-red-500',
        label: 'Llamada saliente',
      },
      'In Use': {
        color: 'text-purple-600',
        dot: 'bg-purple-500',
        label: 'En llamada',
      },
      'In Call': {
        color: 'text-purple-600',
        dot: 'bg-purple-500',
        label: 'En llamada',
      },
      'Not in Use': {
        color: 'text-green-600',
        dot: 'bg-green-500',
        label: 'No en uso',
      },
      Ringing: {
        color: 'text-orange-500',
        dot: 'bg-orange-500',
        label: 'Timbrando',
      },
      Available: {
        color: 'text-green-600',
        dot: 'bg-green-500',
        label: 'Disponible',
      },
      Busy: {
        color: 'text-red-600',
        dot: 'bg-red-500',
        label: 'Ocupado',
      },
      'On Break': {
        color: 'text-yellow-600',
        dot: 'bg-yellow-500',
        label: 'En descanso',
      },
      'Logged Out': {
        color: 'text-gray-500',
        dot: 'bg-gray-400',
        label: 'Desconectado',
      },
      Offline: {
        color: 'text-red-600',
        dot: 'bg-red-400',
        label: 'Offline',
      },
      'Offline (No SIP)': {
        color: 'text-red-600',
        dot: 'bg-red-400',
        label: 'Offline',
      },
    };
    return (
      stateMap[currentState] || { color: 'text-gray-600', dot: 'bg-gray-400', label: currentState }
    );
  };

  // Extract extension number from "user/1000" format
  const extractExtension = (extensionStr: string): string => {
    if (!extensionStr) return '-';
    const parts = extensionStr.split('/');
    return parts.length > 1 ? parts[1] : extensionStr;
  };

  const renderGeneralSummary = (): JSX.Element => (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex-row sm:items-center sm:justify-between flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <h2 className="md:text-2xl text-xl font-bold text-gray-900">
            Resumen de Analíticos del Teléfono
          </h2>
        </div>
        {!isStandalone && (
          <button
            type="button"
            onClick={() => openStandaloneWindow(accountId)}
            className="hover:text-gray-600 sm:self-auto self-end text-gray-400"
            title="Abrir en nueva ventana"
          >
            <ExternalLink size={20} />
          </button>
        )}
      </div>

      {/* Description */}
      <div className="text-sm text-gray-500">
        <p>
          Visibilidad en tiempo real del volumen de llamadas, salud de la cola y actividad de
          agentes.
        </p>
        <p className="mt-1">Actualizado justo ahora - {formatTime(lastUpdated)}</p>
      </div>

      {/* Queue Summary Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Resumen de Cola</h3>

        {/* Call Center Card */}
        <div className="rounded-xl p-6 bg-white border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-50 p-2 rounded-lg">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Centro de Llamadas</h4>
              <p className="text-sm text-gray-500">Rendimiento general de la cola</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="md:grid-cols-4 md:gap-6 grid grid-cols-2 gap-4 mb-6">
            {/* Calls Waiting */}
            <div>
              <p className="mb-1 text-sm text-gray-500">Llamadas en espera</p>
              <p className="md:text-3xl text-2xl font-bold text-gray-900">
                {displayData.callsWaiting}
              </p>
            </div>

            {/* Agents */}
            <div>
              <p className="mb-1 text-sm text-gray-500">Agentes</p>
              <div className="flex items-baseline gap-1">
                <span className="md:text-3xl text-2xl font-bold text-gray-900">
                  {displayData.agentsBusy}
                </span>
                <span className="md:text-lg text-base text-gray-400">
                  / {displayData.agentsAssigned}
                </span>
              </div>
              <p className="text-xs text-gray-400">Ocupados / Asignados</p>
            </div>

            {/* Answered Calls */}
            <div>
              <p className="mb-1 text-sm text-gray-500">Llamadas contestadas</p>
              <p className="md:text-3xl text-2xl font-bold text-gray-900">
                {displayData.answeredCalls}
              </p>
              <p className="text-xs text-gray-400">Hoy</p>
            </div>

            {/* Abandoned Calls */}
            <div>
              <p className="mb-1 text-sm text-gray-500">Llamadas abandonadas</p>
              <div className="flex items-baseline gap-2">
                <span className="md:text-3xl text-2xl font-bold text-gray-900">
                  {displayData.abandonedCalls}
                </span>
                <span className="text-sm text-gray-400">({displayData.abandonedPercentage}%)</span>
              </div>
            </div>
          </div>

          {/* Wait Time Cards */}
          <div className="sm:grid-cols-2 grid grid-cols-1 gap-4">
            {/* Average Wait Time */}
            <div className="bg-gray-50 flex items-center gap-3 p-4 rounded-lg">
              <div className="p-2 bg-white rounded-lg">
                <Clock className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tiempo de Espera Promedio</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatSecondsToTime(displayData.averageWaitTimeSeconds)}
                </p>
              </div>
            </div>

            {/* Maximum Wait Time */}
            <div className="bg-orange-50 flex items-center gap-3 p-4 rounded-lg">
              <div className="p-2 bg-white rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tiempo de Espera Máximo</p>
                <p className="text-xl font-bold text-orange-600">
                  {formatSecondsToTime(displayData.maximumWaitTimeSeconds)}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-3 gap-4 pt-6 mt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{displayData.agentsAvailable}</p>
              <p className="text-xs text-gray-500">Disponibles</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-600">{displayData.agentsOffline}</p>
              <p className="text-xs text-gray-500">Offline</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{displayData.agentsTotal}</p>
              <p className="text-xs text-gray-500">Total Agentes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calls Waiting Table */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Llamadas en Espera</h3>
          <span className="text-sm text-gray-400">Actividad de la cola</span>
        </div>

        <div className="rounded-xl overflow-hidden overflow-x-auto bg-white border border-gray-200">
          <table className="w-full min-w-[480px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Cola
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Posición
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  ID de llamada
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                  Tiempo de espera actual
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {callsWaitingData?.calls?.length ? (
                callsWaitingData.calls.map((call) => (
                  <tr key={call.uuid} className="hover:bg-gray-50">
                    <td className="md:px-6 px-4 py-4 text-sm text-gray-900">{call.queue}</td>
                    <td className="md:px-6 px-4 py-4">
                      <span className="bg-blue-50 inline-flex items-center justify-center w-8 h-6 text-xs font-medium text-blue-700 border border-blue-200 rounded-full">
                        #{call.position}
                      </span>
                    </td>
                    <td className="md:px-6 px-4 py-4 text-sm text-gray-900">{call.caller_id}</td>
                    <td className="md:px-6 px-4 py-4 text-sm text-right text-gray-900">
                      {formatSecondsToTime(call.wait_time_seconds)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="md:px-6 px-4 py-8 text-sm text-center text-gray-400">
                    No hay llamadas en espera
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Agent Status & Performance Table */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Estado y Rendimiento de Agentes</h3>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
              Disponibles: {agentsStatusData?.summary?.available || 0}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
              Ocupados: {agentsStatusData?.summary?.busy || 0}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-full">
              Timbrando: {agentsStatusData?.summary?.ringing || 0}
            </span>
          </div>
        </div>

        <div className="rounded-xl overflow-hidden overflow-x-auto bg-white border border-gray-200">
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Nombre del agente
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Extensión
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Estado actual
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Duración del estado
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Última llamada
                </th>
                <th className="md:px-6 px-4 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                  Llamadas atendidas hoy
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agentsStatusData?.agents?.length ? (
                agentsStatusData.agents.map((agent) => {
                  const stateStyle = getStateStyle(agent.current_state);
                  return (
                    <tr key={agent.agent_id} className="hover:bg-gray-50">
                      <td className="md:px-6 px-4 py-4 text-sm text-gray-900">
                        {agent.agent_name}
                      </td>
                      <td className="md:px-6 px-4 py-4 text-sm text-gray-500">
                        {extractExtension(agent.extension)}
                      </td>
                      <td className="md:px-6 px-4 py-4">
                        <span
                          className={`inline-flex items-center gap-2 text-sm ${stateStyle.color}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${stateStyle.dot}`} />
                          {stateStyle.label}
                        </span>
                      </td>
                      <td className="md:px-6 px-4 py-4 text-sm text-gray-500">
                        {formatSecondsToTime(agent.state_duration_seconds)}
                      </td>
                      <td className="md:px-6 px-4 py-4 text-sm text-gray-500">
                        {formatSecondsAgo(agent.last_call_seconds_ago)}
                      </td>
                      <td className="md:px-6 px-4 py-4 text-sm font-medium text-right text-gray-900">
                        {agent.calls_handled_today}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="md:px-6 px-4 py-8 text-sm text-center text-gray-400">
                    No hay agentes
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Footer */}
          <div className="bg-gray-50 md:px-6 md:text-sm flex flex-wrap items-center justify-center gap-2 px-4 py-3 text-xs text-gray-500 border-t border-gray-200">
            <span>Las métricas reflejan el estado del call center al cargar la página</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Column configurations for each call direction
  type CallDirection = 'outbound' | 'inbound' | 'abandoned';

  interface ColumnConfig {
    title: string;
    showCsv: boolean;
    emptyIcon: React.ReactNode;
    emptyMessage: string;
    columns: { key: string; label: string }[];
    renderRow: (call: CallLog | AbandonedCall) => React.ReactNode;
  }

  const COLUMN_CONFIGS: Record<CallDirection, ColumnConfig> = {
    outbound: {
      title: 'Historial de Llamadas Salientes',
      showCsv: true,
      emptyIcon: <PhoneOff className="w-10 h-10 mx-auto mb-3 text-gray-300" />,
      emptyMessage: 'No se encontraron llamadas',
      columns: [
        { key: 'phoneNumber', label: 'Número de teléfono' },
        { key: 'date', label: 'Fecha' },
        { key: 'time', label: 'Hora' },
        { key: 'duration', label: 'Duración' },
        { key: 'agent', label: 'Agente' },
        { key: 'status', label: 'Estado' },
        { key: 'recording', label: 'Grabación' },
      ],
      renderRow: (call: CallLog | AbandonedCall) => {
        const c = call as CallLog;
        const statusStyle = getCallStatusStyle(c.hangup_cause || c.status || '');
        const phoneNumber = c.destination_number || c.callee_id_number;
        return (
          <>
            <td className="px-6 py-4 text-sm font-medium text-gray-900">{phoneNumber || '-'}</td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatDate(c.start_stamp || c.created_at)}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatTimeFromTimestamp(c.start_stamp || c.created_at)}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatDuration(c.billsec || c.duration || 0)}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {c.agent_name || c.user_name || '-'}
            </td>
            <td className="px-6 py-4">
              <span
                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.color}`}
              >
                {statusStyle.label}
              </span>
            </td>
            <td className="px-6 py-4">
              <RecordingPlayer recordingUrl={c.recording_url} />
            </td>
          </>
        );
      },
    },
    inbound: {
      title: 'Historial de Llamadas Entrantes',
      showCsv: true,
      emptyIcon: <PhoneIncoming className="w-10 h-10 mx-auto mb-3 text-gray-300" />,
      emptyMessage: 'No se encontraron llamadas',
      columns: [
        { key: 'phoneNumber', label: 'Número de teléfono' },
        { key: 'date', label: 'Fecha' },
        { key: 'time', label: 'Hora' },
        { key: 'duration', label: 'Duración' },
        { key: 'agent', label: 'Agente' },
        { key: 'status', label: 'Estado' },
        { key: 'recording', label: 'Grabación' },
      ],
      renderRow: (call: CallLog | AbandonedCall) => {
        const c = call as CallLog;
        const statusStyle = getCallStatusStyle(c.hangup_cause || c.status || '');
        const phoneNumber = c.caller_id_number || c.caller_id_name;
        return (
          <>
            <td className="px-6 py-4 text-sm font-medium text-gray-900">{phoneNumber || '-'}</td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatDate(c.start_stamp || c.created_at)}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatTimeFromTimestamp(c.start_stamp || c.created_at)}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatDuration(c.billsec || c.duration || 0)}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {c.agent_name || c.user_name || '-'}
            </td>
            <td className="px-6 py-4">
              <span
                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.color}`}
              >
                {statusStyle.label}
              </span>
            </td>
            <td className="px-6 py-4">
              <RecordingPlayer recordingUrl={c.recording_url} />
            </td>
          </>
        );
      },
    },
    abandoned: {
      title: 'Llamadas Abandonadas',
      showCsv: false,
      emptyIcon: <PhoneOff className="w-10 h-10 mx-auto mb-3 text-gray-300" />,
      emptyMessage: 'No hay llamadas abandonadas',
      columns: [
        { key: 'callerNumber', label: 'Número llamante' },
        { key: 'destinationNumber', label: 'Número destino' },
        { key: 'module', label: 'Módulo' },
        { key: 'date', label: 'Fecha' },
        { key: 'time', label: 'Hora' },
        { key: 'waitTime', label: 'Tiempo de espera' },
        { key: 'hangupCause', label: 'Causa de colgado' },
      ],
      renderRow: (call: CallLog | AbandonedCall) => {
        const c = call as AbandonedCall;
        return (
          <>
            <td className="px-6 py-4 text-sm font-medium text-gray-900">
              {c.caller_id_number || c.caller_id_name || '-'}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">{c.destination_number || '-'}</td>
            <td className="px-6 py-4 text-sm text-gray-500">{c.module_name || '-'}</td>
            <td className="px-6 py-4 text-sm text-gray-500">{formatDate(c.start_stamp)}</td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatTimeFromTimestamp(c.start_stamp)}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
              {formatDuration(c.wait_time_seconds || c.duration || 0)}
            </td>
            <td className="px-6 py-4">
              <span className="inline-flex px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                {c.hangup_cause || '-'}
              </span>
            </td>
          </>
        );
      },
    },
  };

  const renderCallHistoryTable = (
    calls: CallLog[] | AbandonedCall[],
    isLoadingCalls: boolean,
    hasMore: boolean,
    onLoadMore: () => void,
    direction: CallDirection
  ): JSX.Element => {
    const config = COLUMN_CONFIGS[direction];
    const filtersMap = {
      outbound: outboundFilters,
      inbound: inboundFilters,
      abandoned: abandonedFilters,
    };
    const showFiltersMap = {
      outbound: showOutboundFilters,
      inbound: showInboundFilters,
      abandoned: showAbandonedFilters,
    };
    const toggleFiltersMap = {
      outbound: () => setShowOutboundFilters((p) => !p),
      inbound: () => setShowInboundFilters((p) => !p),
      abandoned: () => setShowAbandonedFilters((p) => !p),
    };
    const filters = filtersMap[direction];
    const showFilters = showFiltersMap[direction];
    const toggleFilters = toggleFiltersMap[direction];
    const hasActiveFilters = Object.values(filters).some((v) => v !== '');

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="md:text-xl text-lg font-bold text-gray-900">{config.title}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleFilters}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                showFilters || hasActiveFilters
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter size={14} />
              Filtros
              {hasActiveFilters && (
                <span className="flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-blue-500 rounded-full">
                  {Object.values(filters).filter((v) => v !== '').length}
                </span>
              )}
            </button>
            {config.showCsv && (
              <button
                type="button"
                onClick={() => handleDownloadCsv(direction as 'outbound' | 'inbound')}
                disabled={isDownloading || calls.length === 0}
                title="Descargar CSV"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
                {isDownloading ? 'Descargando...' : 'Descargar CSV'}
              </button>
            )}
            {!isStandalone && (
              <button
                type="button"
                onClick={() => openStandaloneWindow(accountId)}
                className="hover:text-gray-600 text-gray-400"
                title="Abrir en nueva ventana"
              >
                <ExternalLink size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="rounded-xl p-4 bg-white border border-gray-200">
            <div className="flex flex-wrap items-end gap-3">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <label className="block mb-1 text-xs font-medium text-gray-500">Buscar</label>
                <div className="relative">
                  <Search
                    size={14}
                    className="left-3 top-1/2 absolute text-gray-400 -translate-y-1/2"
                  />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange(direction, 'search', e.target.value)}
                    placeholder="Buscar por número, agente..."
                    className="pl-9 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full py-2 pr-3 text-sm border border-gray-200 rounded-lg"
                  />
                </div>
              </div>

              {/* Date From */}
              <div className="min-w-[150px]">
                <label className="block mb-1 text-xs font-medium text-gray-500">Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange(direction, 'dateFrom', e.target.value)}
                  className="focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>

              {/* Date To */}
              <div className="min-w-[150px]">
                <label className="block mb-1 text-xs font-medium text-gray-500">Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange(direction, 'dateTo', e.target.value)}
                  className="focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>

              {/* Clear filters */}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => handleClearFilters(direction)}
                  className="hover:bg-red-50 inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 transition-colors rounded-lg"
                >
                  <X size={14} />
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        )}

        <div className="rounded-xl overflow-hidden overflow-x-auto bg-white border border-gray-200">
          <table className="w-full min-w-[820px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {config.columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {calls.length > 0 ? (
                calls.map((call, index) => {
                  const callId =
                    'uuid' in call && call.uuid
                      ? call.uuid
                      : 'id' in call && call.id != null
                      ? call.id
                      : index;
                  return (
                    <tr key={callId} className="hover:bg-gray-50">
                      {config.renderRow(call)}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={config.columns.length}
                    className="px-6 py-12 text-sm text-center text-gray-400"
                  >
                    {isLoadingCalls ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 size={20} className="animate-spin text-gray-400" />
                        <span>Cargando...</span>
                      </div>
                    ) : (
                      <>
                        {config.emptyIcon}
                        {config.emptyMessage}
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Load more button */}
          {calls.length > 0 && hasMore && (
            <div className="bg-gray-50 px-6 py-3 text-center border-t border-gray-200">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={isLoadingCalls}
                className="hover:text-blue-800 disabled:text-gray-400 px-4 py-2 text-sm font-medium text-blue-600"
              >
                {isLoadingCalls ? 'Cargando...' : 'Cargar más'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderOutboundHistory = (): JSX.Element =>
    renderCallHistoryTable(
      outboundCalls,
      isLoadingOutbound,
      outboundHasMore,
      () => {
        void fetchOutboundCallsData(outboundPage + 1, false, outboundFilters);
      },
      'outbound'
    );

  const renderInboundHistory = (): JSX.Element =>
    renderCallHistoryTable(
      inboundCalls,
      isLoadingInbound,
      inboundHasMore,
      () => {
        void fetchInboundCallsData(inboundPage + 1, false, inboundFilters);
      },
      'inbound'
    );

  const renderAbandonedHistory = (): JSX.Element =>
    renderCallHistoryTable(
      abandonedCalls,
      isLoadingAbandoned,
      abandonedHasMore,
      () => {
        void fetchAbandonedCallsData(abandonedPage + 1, false, abandonedFilters);
      },
      'abandoned'
    );

  return (
    <div
      className={classNames(
        'px-4 py-4 md:px-6 md:py-6',
        isStandalone
          ? 'min-h-screen bg-gray-50'
          : 'flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50'
      )}
    >
      {/* Sub-tabs */}
      <div className="flex mb-6 overflow-x-auto">
        <div className="whitespace-nowrap inline-flex p-1 bg-gray-100 rounded-lg">
          <button
            type="button"
            onClick={() => handleSetView(PHONE_ANALYTICS_VIEW.GENERAL_SUMMARY)}
            className={`px-3 py-2 text-sm md:px-4 md:text-base rounded-md transition-colors ${
              currentView === PHONE_ANALYTICS_VIEW.GENERAL_SUMMARY
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Resumen General
          </button>
          <button
            type="button"
            onClick={() => handleSetView(PHONE_ANALYTICS_VIEW.OUTBOUND_HISTORY)}
            className={`px-3 py-2 text-sm md:px-4 md:text-base rounded-md transition-colors ${
              currentView === PHONE_ANALYTICS_VIEW.OUTBOUND_HISTORY
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Historial de Llamadas Salientes
          </button>
          <button
            type="button"
            onClick={() => handleSetView(PHONE_ANALYTICS_VIEW.INBOUND_HISTORY)}
            className={`px-3 py-2 text-sm md:px-4 md:text-base rounded-md transition-colors ${
              currentView === PHONE_ANALYTICS_VIEW.INBOUND_HISTORY
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Historial de Llamadas Entrantes
          </button>
          <button
            type="button"
            onClick={() => handleSetView(PHONE_ANALYTICS_VIEW.ABANDONED_CALLS)}
            className={`px-3 py-2 text-sm md:px-4 md:text-base rounded-md transition-colors ${
              currentView === PHONE_ANALYTICS_VIEW.ABANDONED_CALLS
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Llamadas Abandonadas
          </button>
        </div>
      </div>

      {/* Content based on active view */}
      {currentView === PHONE_ANALYTICS_VIEW.GENERAL_SUMMARY && renderGeneralSummary()}
      {currentView === PHONE_ANALYTICS_VIEW.OUTBOUND_HISTORY && renderOutboundHistory()}
      {currentView === PHONE_ANALYTICS_VIEW.INBOUND_HISTORY && renderInboundHistory()}
      {currentView === PHONE_ANALYTICS_VIEW.ABANDONED_CALLS && renderAbandonedHistory()}
    </div>
  );
};
